import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { lighten } from '@material-ui/core';
import GrowingProgressBar from 'Components/UI/GrowingProgressBar';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { fetchGoals, removeGoal, saveGoal } from 'Helpers/Goals';
import relationships from 'Consts/Relationships';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Moment from 'moment';
import WellnessView from './WellnessView';
import GoalsView from './GoalsView';
import Investments from './Investments';
import Tab from './Tab';

const styles = theme => ({
  root: {
    display: 'grid',
    gridTemplateRows: '100%',
    gridTemplateColumns: '200px 1fr',
    gridTemplateAreas: `
    'tabs content'
    `,
    placeItems: 'stretch',
    height: '100%',
  },
  content: {
    maxHeight: '100%',
    gridArea: 'content',
    flex: 1,
  },
  indicator: {
    left: '0 !important',
    width: '4px',
    backgroundColor: theme.palette.facet.accentBlue7,
  },
  tabs: {
    gridArea: 'tabs',
    backgroundColor: lighten(theme.palette.facet.lightGray3, 0.7),
    boxShadow: 'inset -10px 0 5px -5px hsla(0,0%,0%,.15)',
    minWidth: '200px',
  },
  tabPanel: {
    overflowY: 'auto',
    display: 'flex',
    flex: 1,
  },
  error: {
    color: theme.palette.error.main,
  },
});

const PlanningDataTab = ({ classes, className }) => {
  const { householdResource, householdId } = useSimpleHousehold();
  const {
    collection: goalCollection,
    setCollection: setGoalCollection,
  } = useCollection();
  const {
    collection: financialWellnessCollection,
    setCollection: setFinancialWellnessCollection,
  } = useCollection();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isScoresError, setIsScoresError] = useState(false);
  const goalResources = goalCollection?.resources().list() || [];

  useEffect(() => {
    const getWellnessScoresLen = async () => {
      try {
        const wellnessColl = await householdResource
          .relationship('financialWellness')
          .link('related')
          .get();
        const scoresResource = wellnessColl
          .resources()
          .head()
          .attr('components')
          .resources()
          .list();

        let scoresCount = 0;
        for (const score of scoresResource) {
          const value = score
            .attr('values')
            .resources()
            .head()
            .attr('value');
          if (value != null) {
            scoresCount += 1;
          }
        }

        setIsScoresError(false);
        setProgress((scoresCount / scoresResource.length) * 100);
      } catch (err) {
        setIsScoresError(true);
      }
    };

    getWellnessScoresLen();
  }, []);

  const fetchTabData = async () => {
    if (householdResource) {
      fetchGoals(setGoalCollection, householdResource);
      const wellnessCollection = await householdResource
        .relationship('financialWellness')
        .link('related')
        .get();
      setFinancialWellnessCollection(wellnessCollection);
    }
  };

  const getGoals = () => {
    if (householdResource?.relationship(relationships.goals).link('related')) {
      fetchGoals(setGoalCollection, householdResource);
    }
  };

  const addGoal = () => {
    const newResource = goalCollection.create();
    newResource.attributes({
      type: goalCollection
        .meta()
        .field('type')
        .export().options[0].id,
      timeHorizonValue: goalCollection
        .meta()
        .field('timeHorizonValue')
        .export().options[0].id,
      timeHorizonType: goalCollection
        .meta()
        .field('timeHorizonType')
        .export().options[0].id,
      priority: goalCollection
        .meta()
        .field('priority')
        .export().options[0].id,
    });

    return newResource
      .submit()
      .then(getGoals)
      .catch(err => displayErrorMessage(err, err.errors));
  };

  const saveGoalToCollection = goal => saveGoal(goal, setGoalCollection, getGoals);
  const removeGoalFromCollection = goalId => removeGoal(goalId, goalResources);

  const [goalMostRecentUpdatedTime] = goalResources
    .map(resource => resource.attr('updatedAt'))
    .sort((a, b) => new Moment(b) - new Moment(a));

  const isMissingGoalsData = goalResources.some(resource => resource.attr('isMissingData') === 'true');

  const wellnessMostRecentUpdatedTime = financialWellnessCollection
    ?.resources()
    ?.head()
    ?.attr('lastChangedDate');

  useEffect(() => {
    fetchTabData();
  }, [householdResource]);

  return (
    <div className={classNames(classes.root, className)}>
      <TabContext value={activeIndex}>
        <TabList
          className={classes.tabs}
          orientation="vertical"
          variant="scrollable"
          value={activeIndex}
          onChange={(event, newValue) => setActiveIndex(newValue)}
          TabIndicatorProps={{
            className: classes.indicator,
          }}
        >
          <Tab
            label="Wellness"
            progress={progress}
            isScoresError={isScoresError}
            lastUpdateTime={wellnessMostRecentUpdatedTime}
            SubtitleComponent={
              <div style={{ width: '100%' }}>
                {isScoresError ? (
                  <div className={classes.error}>
                    Error fetching wellness data
                  </div>
                ) : (
                  <GrowingProgressBar label="Data" progress={progress} />
                )}
              </div>
              }
          />
          <Tab
            label="Goals"
            isMissingData={isMissingGoalsData}
            lastUpdateTime={goalMostRecentUpdatedTime}
          />
          <Tab label="Investments" />
        </TabList>
        <TabPanel className={classes.tabPanel} value={0}>
          <WellnessView
            financialWellnessCollection={financialWellnessCollection}
          />
        </TabPanel>
        <TabPanel className={classes.tabPanel} value={1}>
          <GoalsView
            goalCollection={goalCollection}
            goalResources={goalResources}
            setGoalCollection={setGoalCollection}
            householdId={Number(householdId)}
            addGoal={addGoal}
            saveGoalToCollection={saveGoalToCollection}
            removeGoalFromCollection={removeGoalFromCollection}
          />
        </TabPanel>
        <TabPanel className={classes.tabPanel} value={2}>
          <Investments />
        </TabPanel>
      </TabContext>
    </div>
  );
};

PlanningDataTab.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

PlanningDataTab.defaultProps = {
  className: '',
};

export default withStyles(styles)(PlanningDataTab);
